<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('employeeMission.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('employeeMission.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">
            {{ $t('add_new') }}
          </ts-button>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-tooltip="$t('employeeMission.searchPlaceholder')"
              v-model="search"
              :placeholder="$t('employeeMission.searchPlaceholder')"
              style="width: 250px"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table :columns="columns" :records="resources" :loading="loading">
          <template v-slot="{ record }">
            <td class="ts-text-center">{{ record.employee_name }}</td>
            <td class="ts-text-center">{{ record.mission_type_name }}</td>
            <td class="ts-text-center">{{ record.mission_title }}</td>
            <td class="ts-text-center">{{ record.start_date }}</td>
            <td class="ts-text-center">{{ record.end_date }}</td>
            <td class="ts-text-center">{{ record.total_day || 0 }}</td>
            <td class="ts-text-center">{{ record.meal_allowance || 0 }}</td>
            <td class="ts-text-center">
              {{ record.accommodation_allowance || 0 }}
            </td>
            <td class="ts-text-center">
              {{ record.day_return_allowance || 0 }}
            </td>
            <td class="ts-text-center">{{ record.advance_amount || 0 }}</td>
            <td class="ts-text-center">{{ record.approved_by_name }}</td>
            <td class="ts-text-center">{{ record.assigned_by_name }}</td>
            <td class="text-center ts-space-x-3">
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(record)"
              >
                <a href="#" :disabled="record._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="record._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData" />
      </div>
      <!-- form-action -->
      <a-modal
        v-if="showForm"
        v-model="showForm"
        :title="$t('employeeMission.pageTitle')"
        centered
        @cancel="clearEdit"
        :zIndex="1020"
        :footer="null"
        :width="800"
      >
        <from-action
          @cancel="() => (showForm = false)"
          @fetchData="fetchData"
        />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'

export default {
  name: 'EmployeeMission',
  components: {
    FromAction
  },
  data() {
    return {
      loading: false,
      showForm: false
    }
  },
  computed: {
    ...mapState('mission/employeeMission', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.mission.employeeMission.search
      },
      set(newValue) {
        this.$store.commit('mission/employeeMission/SET_SEARCH', newValue)
        this.$store.commit('mission/employeeMission/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          name: this.$t('employeeMission.employee'),
          style: 'text-align:center; min-width:150px'
        },
        {
          name: this.$t('employeeMission.missionType'),
          style: 'text-align:center; min-width:300px'
        },
        {
          name: this.$t('employeeMission.missionTitle'),
          style: 'text-align:center; min-width:200px'
        },
        {
          name: this.$t('employeeMission.startDate'),
          style: 'text-align:center; min-width:120px'
        },
        {
          name: this.$t('employeeMission.endDate'),
          style: 'text-align:center; min-width:120px'
        },
        {
          name: this.$t('employeeMission.totalDays'),
          style: 'text-align:center; min-width:100px'
        },
        {
          name: this.$t('employeeMission.mealAllowance'),
          style: 'text-align:right; min-width:130px'
        },
        {
          name: this.$t('employeeMission.accommodationAllowance'),
          style: 'text-align:right; min-width:180px'
        },
        {
          name: this.$t('employeeMission.dayReturnAllowance'),
          style: 'text-align:right; min-width:150px'
        },
        {
          name: this.$t('employeeMission.advanceAmount'),
          style: 'text-align:right; min-width:130px'
        },
        {
          name: this.$t('employeeMission.approvedBy'),
          style: 'text-align:center; min-width:150px'
        },
        {
          name: this.$t('employeeMission.assignedBy'),
          style: 'text-align:center; min-width:150px'
        },
        {
          name: this.$t('actions'),
          style: 'text-align:center; min-width:60px'
        }
      ]
    }
  },
  methods: {
    async fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('mission/employeeMission/fetch', attributes)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.$store.commit('mission/employeeMission/SET_EDIT_DATA', {})
      this.$nextTick(() => {
        this.showForm = true
      })
    },
    edit(record) {
      this.$store.commit('mission/employeeMission/SET_EDIT_DATA', record)
      this.$nextTick(() => {
        this.showForm = true
      })
    },
    clearEdit() {
      this.$store.commit('mission/employeeMission/SET_EDIT_DATA', {})
      this.showForm = false
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('mission/employeeMission/destroy', record.id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('mission/employeeMission/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  }
}
</script>
